import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Form,
  Button,
  Spinner,
  ListGroup,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { searchICD10 } from "../../afinanceapi";
import "./SearchICD10.css";

export default function ICD10PickerModal({
  show,
  onHide,
  onConfirm,
  initialSelected = [],
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // normalize incoming
  const [selected, setSelected] = useState([]);

  // Helper: stable key for ICD record
  const codeKeyOf = (obj) => obj?.ICD10_Code || obj?.ICD10_3_Code || "";

  const isHydrated = (icd) =>
    !!(
      icd?.Group_Code ||
      icd?.Group_Desc ||
      icd?.ICD10_3_Description ||
      icd?.ICD10_Description
    );
  // 1) Keep local state in sync when the modal opens or the prop changes
  useEffect(() => {
    if (!show) return;
    const base = (initialSelected || []).map((s) => ({
      item: s.item ?? s,
      isPrimary: !!s.isPrimary,
    }));
    setSelected(base);
  }, [show, initialSelected]);

  // 2) Hydrate minimal items (codes only) to full ICD records so labels render nicely
  useEffect(() => {
    if (!show || !selected.length) return;
    let cancelled = false;
    (async () => {
      const updated = await Promise.all(
        selected.map(async (s) => {
          const item = s.item ?? s;
          if (isHydrated(item)) return s;
          const key = codeKeyOf(item);
          if (!key) return s;
          try {
            const hits = await searchICD10(key);
            const list = Array.isArray(hits) ? hits : [];
            const exact = list.find(
              (r) => r.ICD10_Code === key || r.ICD10_3_Code === key
            );
            return exact ? { ...s, item: exact } : s;
          } catch {
            return s;
          }
        })
      );
      if (!cancelled) setSelected(updated);
    })();
    return () => {
      cancelled = true;
    };
  }, [show, selected.length]); // re-run on open if there are selections

  // Dedupe set
  const selectedCodes = useMemo(
    () => new Set(selected.map((s) => codeKeyOf(s.item ?? s))),
    [selected]
  );

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await searchICD10(query.trim());
        if (!cancelled) setResults(Array.isArray(res) ? res : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);

  const addICD = (item) => {
    const key = codeKeyOf(item);
    if (!key || selectedCodes.has(key)) return;

    const canBePrimary = item?.ValidPrimary === "Y";
    const isFirst = selected.length === 0;

    setSelected((prev) => [
      ...prev,
      {
        item,
        // only auto-primary if it's the first AND allowed
        isPrimary: isFirst && canBePrimary,
      },
    ]);

    setQuery("");
    setResults([]);
  };

  const removeICD = (codeKey) => {
    setSelected((prev) =>
      prev.filter((s) => codeKeyOf(s.item ?? s) !== codeKey)
    );
  };

  // Toggle primary (checkbox -> allow multiple primaries)
  const togglePrimary = (codeKey) => {
    setSelected((prev) =>
      prev.map((s) =>
        codeKeyOf(s.item ?? s) === codeKey
          ? { ...s, isPrimary: !s.isPrimary }
          : s
      )
    );
  };

  const clearAll = () => setSelected([]);
  const handleConfirm = () => onConfirm(selected);

  const Truncated = ({ children, title }) => (
    <div className="text-truncate" title={title} style={{ maxWidth: "100%" }}>
      {children}
    </div>
  );

  const renderICDLine = (icd) => {
    const groupCode = icd.Group_Code ?? "";
    const groupDesc = icd.Group_Desc ?? "";
    const icd3Code = icd.ICD10_3_Code ?? "";
    const icd3Desc = icd.ICD10_3_Description ?? "";
    const code = icd.ICD10_Code ?? "";
    const desc = icd.ICD10_Description ?? "";
    const line = `${icd3Code} - ${icd3Desc} | ${code} - ${desc} | ${groupCode} - ${groupDesc}`;
    return (
      <Truncated title={line}>
        {icd3Code} - {icd3Desc} | <strong>{code || icd3Code}</strong> - {desc} |{" "}
        {groupCode} - {groupDesc}
      </Truncated>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      // fullscreen="md-down"
      scrollable
      centered={false}
      dialogClassName="icd10-modal"
    >
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Select ICD-10 Codes
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="py-3">
        <Form.Control
          type="text"
          placeholder="Search by chapter, description, or code (e.g. M54.5)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="mb-3"
          size="sm"
          autoFocus
        />

        {loading && (
          <div className="d-flex align-items-center gap-2 mb-2">
            <Spinner animation="border" size="sm" /> <span>Searching…</span>
          </div>
        )}

        {results.length > 0 && (
          <ListGroup className="mb-3">
            {results.map((item, idx) => {
              const key = codeKeyOf(item);
              const isAlready = selectedCodes.has(key);
              return (
                <ListGroup.Item
                  action
                  key={key ? key : `row-${idx}`}
                  onClick={() => addICD(item)}
                  disabled={isAlready || !key}
                  className="py-2"
                >
                  <Row className="g-2 align-items-center">
                    <Col xs="auto">
                      <Badge
                        bg={
                          isAlready ? "secondary" : key ? "primary" : "warning"
                        }
                      >
                        {isAlready ? "Added" : key ? "Add" : "No code"}
                      </Badge>
                    </Col>
                    <Col xs className="text-wrap">
                      {renderICDLine(item)}
                    </Col>
                  </Row>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        )}

        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="m-0">Selected ICD-10</h6>
          <div className="text-muted small">{selected.length} selected</div>
        </div>

        {selected.length === 0 ? (
          <div className="text-muted">
            No codes selected yet. Use the search above to add.
          </div>
        ) : (
          <ListGroup>
            {selected.map((s, idx) => {
              const key = codeKeyOf(s.item ?? s);
              return (
                <ListGroup.Item key={key || `sel-${idx}`} className="py-2">
                  <Row className="g-2">
                    <Col xs={12} className="min-w-0">
                      <div className="icd-line">{renderICDLine(s.item)}</div>
                    </Col>
                    <Col
                      xs={12}
                      className="d-flex justify-content-between align-items-center mt-1"
                    >
                      <Form.Check
                        type="checkbox"
                        id={`primary-${key || idx}`}
                        checked={!!s.isPrimary}
                        onChange={() => togglePrimary(key)}
                        label={
                          s.item?.ValidPrimary === "N" ? (
                            <span className="text-muted">
                              Not allowed as a primary ICD10
                            </span>
                          ) : (
                            "Primary"
                          )
                        }
                        className="mb-0"
                        disabled={s.item?.ValidPrimary === "N"}
                      />

                      <Button
                        variant=""
                        size="sm"
                        onClick={() => removeICD(key)}
                        aria-label="Remove code"
                      >
                        <i className="fa-solid fa-trash-can text-danger"></i>
                      </Button>
                    </Col>
                  </Row>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button
          variant="outline-secondary"
          onClick={clearAll}
          disabled={!selected.length}
        >
          Clear
        </Button>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!selected.length}
          >
            Use {selected.length} code{selected.length > 1 ? "s" : ""}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
